import { Box, Container, Grid, Typography } from "@mui/material";
import React from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import DevicesIcon from "@mui/icons-material/Devices";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

const descCols = [
  {
    icon: <GroupsIcon sx={{ fontSize: 32 }} />,
    heading: "5,000+",
    desc: "Students empowered with AI-driven academic support and performance tracking",
    gradient: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
    glow: "rgba(79,110,247,0.35)",
  },
  {
    icon: <DevicesIcon sx={{ fontSize: 32 }} />,
    heading: "Unified",
    desc: "Complete school management and parent engagement in one seamless SaaS & mobile ecosystem",
    gradient: "linear-gradient(135deg, #0EA5E9 0%, #4F6EF7 100%)",
    glow: "rgba(14,165,233,0.35)",
  },
  {
    icon: <ConnectWithoutContactIcon sx={{ fontSize: 32 }} />,
    heading: "95%",
    desc: "Parent engagement powered by real-time updates and live transport visibility",
    gradient: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
    glow: "rgba(16,185,129,0.35)",
  },
  {
    icon: <AutoFixHighIcon sx={{ fontSize: 32 }} />,
    heading: "70%",
    desc: "Administrative workload reduced through automation and intelligent workflows",
    gradient: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
    glow: "rgba(245,158,11,0.35)",
  },
];

const DesSec = () => {
  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(180deg, #0A0F2C 0%, #0D1B4B 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background grid pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(79,110,247,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79,110,247,0.05) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Top glow */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          left: "50%",
          transform: "translateX(-50%)",
          width: 800,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(79,110,247,0.12) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 8, md: 10 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.8,
              borderRadius: "100px",
              bgcolor: "rgba(79,110,247,0.15)",
              border: "1px solid rgba(79,110,247,0.3)",
              mb: 3,
            }}
          >
            <Typography fontSize="0.82rem" fontWeight={600} color="#a5b4fc" letterSpacing="0.06em">
              PLATFORM IMPACT
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              color: "#fff",
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 900,
              mb: 2.5,
            }}
          >
            Impact Powered by Intelligence
          </Typography>

          <Typography
            variant="h6"
            sx={{
              maxWidth: 700,
              mx: "auto",
              color: "#94a3b8",
              lineHeight: 1.8,
              fontWeight: 400,
              fontSize: { xs: "0.95rem", md: "1.05rem" },
            }}
          >
            MySchoolling combines AI, automation, and real-time engagement
            to deliver measurable outcomes across schools and families.
          </Typography>
        </Box>

        {/* Stats grid */}
        <Grid container spacing={3} justifyContent="center">
          {descCols.map((item, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Box
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: "24px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  backdropFilter: "blur(12px)",
                  textAlign: "center",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "default",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow: `0 30px 80px ${item.glow}`,
                  },
                }}
              >
                {/* Glow orb */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: item.gradient,
                    opacity: 0.08,
                    filter: "blur(20px)",
                    pointerEvents: "none",
                  }}
                />

                {/* Icon */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 60,
                    height: 60,
                    borderRadius: "16px",
                    background: item.gradient,
                    mb: 3,
                    boxShadow: `0 8px 24px ${item.glow}`,
                    color: "#fff",
                  }}
                >
                  {item.icon}
                </Box>

                {/* Stat number */}
                <Typography
                  sx={{
                    fontSize: { xs: "2.4rem", md: "2.8rem" },
                    fontWeight: 900,
                    lineHeight: 1,
                    mb: 2,
                    background: item.gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {item.heading}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#94a3b8",
                    lineHeight: 1.75,
                    fontSize: "0.9rem",
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default DesSec;
